/**
 * Controle de validades — replicado do sistema antigo.
 *
 * Três blocos, como lá:
 *   1. Vencidos a descartar — confere-se fisicamente e clica-se "Descartado":
 *      o lote some daqui e a quantidade é abatida do saldo (saída "descarte").
 *   2. Vencendo em até 60 dias — o que merece prioridade no atendimento.
 *   3. Todos os lotes — busca + filtro, ordenado do mais urgente.
 *
 * Cada linha é um LOTE (movimento de entrada com validade): o mesmo item
 * aparece várias vezes com marcas/validades diferentes. As fotos vêm
 * embutidas no JSON (miniatura data URI) — a lista desenha completa de uma
 * vez, sem requisição de imagem.
 */

import { api, ErroApi } from '../api.js';
import { escapar, numero } from '../comum/formato.js';
import { avisar, avisarBloqueante } from '../comum/aviso.js';

type StatusLote = 'vencido' | 'vencendo' | 'atencao' | 'alerta60' | 'ok';

interface Lote {
  id: number;
  codigo: string;
  nome: string;
  unidade: string | null;
  saldo: number;
  quantidade: number;
  fornecedor: string | null;
  lote: string | null;
  marca: string | null;
  validade: string;
  dias: number;
  status: StatusLote;
  miniatura: string | null;
  foto: string | null;
}

const STATUS: Record<StatusLote, { rotulo: string; classe: string }> = {
  vencido: { rotulo: 'Vencido', classe: 'v-vencido' },
  vencendo: { rotulo: 'Vencendo (≤7d)', classe: 'v-vencendo' },
  atencao: { rotulo: 'Atenção (≤30d)', classe: 'v-atencao' },
  alerta60: { rotulo: 'Alerta (≤60d)', classe: 'v-alerta' },
  ok: { rotulo: 'Ok', classe: 'v-ok' },
};

function dataBr(iso: string): string {
  const [a, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${a}`;
}

export function montarValidades(raiz: HTMLElement): void {
  let lotes: Lote[] = [];

  raiz.innerHTML = `<p class="carregando">Carregando os lotes…</p>`;

  async function carregar(): Promise<void> {
    try {
      lotes = (await api.get<{ lotes: Lote[] }>('/api/validades')).lotes;
      desenhar();
    } catch (erro) {
      raiz.innerHTML = `<p class="aviso">${escapar(
        erro instanceof ErroApi ? erro.message : 'Não consegui carregar as validades.',
      )}</p>`;
    }
  }

  function desenhar(): void {
    const vencidos = lotes.filter((l) => l.status === 'vencido');
    const chegando = lotes.filter((l) => l.dias >= 0 && l.dias <= 60);

    raiz.innerHTML = `
      <section class="ped-painel">
        <h3>🗑️ Vencidos — a descartar <span class="sub-inline">${vencidos.length} lote(s)</span></h3>
        ${
          vencidos.length === 0
            ? `<p class="dica">Nenhum lote vencido. 👌</p>`
            : `<p class="dica">Confira o material fisicamente e clique em "Descartado" para dar
                 baixa no estoque — o lote sai desta lista e a quantidade é abatida do saldo.</p>
               <div id="val-vencidos">${vencidos.map(cartaoVencido).join('')}</div>`
        }
      </section>

      <section class="ped-painel">
        <h3>⚠️ Vencendo em até 60 dias <span class="sub-inline">${chegando.length} lote(s)</span></h3>
        ${
          chegando.length === 0
            ? `<p class="dica">Nada vencendo nos próximos 60 dias.</p>`
            : tabela(chegando, false)
        }
      </section>

      <section class="ped-painel">
        <h3>Todos os lotes com validade <span class="sub-inline">${lotes.length} lote(s)</span></h3>
        <p class="dica">Cada linha é um lote recebido — o mesmo item pode aparecer várias
          vezes, com marcas e validades diferentes. Do mais urgente para o mais folgado.</p>
        <div class="barra-busca val-filtros">
          <input id="val-busca" type="search" placeholder="Buscar por código, nome, lote, marca ou fornecedor…" autocomplete="off">
          <select id="val-filtro">
            <option value="">Todos</option>
            <option value="vencido">Vencidos</option>
            <option value="vencendo">Vencendo (≤7d)</option>
            <option value="atencao">Atenção (≤30d)</option>
            <option value="alerta60">Alerta (≤60d)</option>
          </select>
        </div>
        <div id="val-todos">${tabela(lotes, true)}</div>
      </section>`;

    raiz.querySelector('#val-busca')!.addEventListener('input', filtrar);
    raiz.querySelector('#val-filtro')!.addEventListener('change', filtrar);
    raiz.querySelectorAll<HTMLButtonElement>('[data-descartar]').forEach((b) => {
      b.addEventListener('click', () => void descartar(Number(b.dataset['descartar'])));
    });
  }

  function cartaoVencido(l: Lote): string {
    return `
      <div class="val-card">
        <span class="ped-cfoto">${
          l.miniatura
            ? `<img src="${l.miniatura}" alt="" ${l.foto ? `data-grande="${escapar(api.foto(l.foto) ?? '')}" data-fotos-de="${escapar(l.codigo)}"` : ''}>`
            : '<span class="sem">—</span>'
        }</span>
        <span class="ped-cnome">
          <b>${escapar(l.nome)}</b>
          <span class="sub-inline">${escapar(l.codigo)}${l.lote ? ` · lote ${escapar(l.lote)}` : ''}${
            l.marca ? ` · ${escapar(l.marca)}` : ''
          }</span>
          <span class="val-venceu">venceu em ${dataBr(l.validade)} (${Math.abs(l.dias)} dia(s) atrás) ·
            ${numero(l.quantidade)} ${escapar(l.unidade ?? '')}</span>
        </span>
        <button class="btn btn-claro" data-descartar="${l.id}">🗑️ Descartado</button>
      </div>`;
  }

  function tabela(quais: Lote[], comStatus: boolean): string {
    return `
      <div class="rolagem">
        <table class="tabela">
          <thead><tr>
            <th></th><th>Código</th><th>Item</th><th>Lote</th><th>Marca</th>
            <th>Fornecedor</th><th class="num">Qtd.</th><th>Validade</th>
            <th class="num">Dias</th>${comStatus ? '<th>Status</th>' : ''}
          </tr></thead>
          <tbody>
            ${quais
              .map((l) => {
                const s = STATUS[l.status];
                return `
              <tr data-lote>
                <td class="val-tfoto">${
                  l.miniatura
                    ? `<img src="${l.miniatura}" alt="" ${l.foto ? `data-grande="${escapar(api.foto(l.foto) ?? '')}" data-fotos-de="${escapar(l.codigo)}"` : ''}>`
                    : ''
                }</td>
                <td class="cod">${escapar(l.codigo)}</td>
                <td>${escapar(l.nome)}</td>
                <td>${escapar(l.lote ?? '—')}</td>
                <td>${escapar(l.marca ?? '—')}</td>
                <td>${escapar(l.fornecedor ?? '—')}</td>
                <td class="num">${numero(l.quantidade)} ${escapar(l.unidade ?? '')}</td>
                <td>${dataBr(l.validade)}</td>
                <td class="num">${l.dias}</td>
                ${comStatus ? `<td><span class="val-pill ${s.classe}">${s.rotulo}</span></td>` : ''}
              </tr>`;
              })
              .join('')}
          </tbody>
        </table>
      </div>`;
  }

  /** Busca e filtro são locais — os lotes já estão todos aqui, nada de rede. */
  function filtrar(): void {
    const termo = raiz
      .querySelector<HTMLInputElement>('#val-busca')!
      .value.trim()
      .toLowerCase();
    const status = raiz.querySelector<HTMLSelectElement>('#val-filtro')!.value;
    const quais = lotes.filter((l) => {
      if (status && l.status !== status) return false;
      if (!termo) return true;
      return [l.codigo, l.nome, l.lote, l.marca, l.fornecedor]
        .some((c) => c?.toLowerCase().includes(termo));
    });
    raiz.querySelector<HTMLElement>('#val-todos')!.innerHTML = quais.length
      ? tabela(quais, true)
      : `<p class="dica">Nenhum lote com esse recorte.</p>`;
  }

  async function descartar(id: number): Promise<void> {
    const l = lotes.find((x) => x.id === id);
    if (!l) return;
    // O que resta do lote não é rastreado: a pessoa diz o que jogou fora, com o
    // menor entre o lote e o estoque como sugestão — e nunca acima do estoque
    // (16/09, Lucas: descarte deixava saldo negativo).
    const un = l.unidade ?? '';
    const maximo = Math.max(0, Math.min(l.quantidade, l.saldo));
    const resposta = window.prompt(
      `Descartar o lote de "${l.nome}" (${l.codigo}).\n` +
        `O lote tinha ${numero(l.quantidade)} ${un}; o estoque do item hoje é ${numero(l.saldo)} ${un}.\n\n` +
        `Quanto foi jogado fora? (0 se nada restava — o lote só sai da lista)`,
      String(maximo).replace('.', ','),
    );
    if (resposta === null) return;
    const quantidade = Number(resposta.trim().replace(/\./g, '').replace(',', '.'));
    if (!Number.isFinite(quantidade) || quantidade < 0) return avisarBloqueante('Digite a quantidade descartada (0 se nada restava).');
    try {
      const r = await api.post<{ saldo: number; abatido: number; aviso: string | null }>(`/api/validades/${id}/descarte`, { quantidade });
      avisar(`Lote descartado — ${numero(r.abatido)} ${un} abatidos; saldo de ${l.codigo} agora é ${numero(r.saldo)}.`);
      if (r.aviso) avisarBloqueante(r.aviso);
      await carregar();
    } catch (e) {
      avisarBloqueante(e instanceof ErroApi ? `${e.message}${e.sugestao ? `\n\n${e.sugestao}` : ''}` : 'Não consegui registrar o descarte.');
    }
  }

  void carregar();
}
